/**
 * Central copy for page headers: title, description, what you can do, what you should do.
 * Key by route path or logical name for dynamic routes.
 */

export const pageDescriptions = {
  '/': {
    title: 'Dashboard',
    description: 'Your overview of resumes, jobs, and application progress.',
    whatYouCanDo: [
      'View resume strength and key stats at a glance',
      'Jump to Resumes, Job Board, or Applications',
      'See recommended jobs and recent activity',
    ],
    whatYouShouldDo: [
      'Keep your Professional Profile updated',
      'Apply to saved jobs and update application status',
      'Use Resume Polish before sending applications',
    ],
  },
  '/profile': {
    title: 'Professional Profile',
    description: 'Manage your professional information and career preferences.',
    whatYouCanDo: [
      'Edit contact info, experience, and education',
      'Add and manage career directions',
      'Link resumes to career directions',
    ],
    whatYouShouldDo: [
      'Keep your profile current for better recommendations',
      'Add multiple career directions for tailored resumes',
      'Complete experience and education for stronger matches',
    ],
  },
  '/career-direction': {
    title: 'Career Direction',
    description: 'Choose and refine career directions to build tailored resumes and get better job matches.',
    whatYouCanDo: [
      'View and select career directions based on your profile',
      'Build or update resumes for each direction',
      'Get job recommendations aligned to your direction',
    ],
    whatYouShouldDo: [
      'Select at least one primary career direction',
      'Keep directions updated as your goals change',
      'Use Massage Resume to tailor a resume to a specific job',
    ],
  },
  '/resumes': {
    title: 'My Resumes',
    description: 'Manage all your resumes. Upload, edit, polish, and compare versions.',
    whatYouCanDo: [
      'Upload new resumes and organize by name',
      'Edit sections, clone, or delete resumes',
      'Use Polish to improve wording and Run Massage for job-specific versions',
      'Compare two resumes side by side',
    ],
    whatYouShouldDo: [
      'Keep one strong resume per target role or direction',
      'Run Polish before applying to improve ATS and readability',
      'Use Massage when applying to a specific job for better fit',
    ],
  },
  '/resumes/upload': {
    title: 'Upload Resume',
    description: 'Add a new resume to your account. We support PDF and common formats.',
    whatYouCanDo: [
      'Upload a PDF or document resume',
      'Name and organize the resume for later use',
    ],
    whatYouShouldDo: [
      'Use a clear, up-to-date resume file',
      'Give it a descriptive name (e.g. role or version)',
    ],
  },
  resumeEditor: {
    title: 'Edit Resume',
    description: 'Edit sections and content of this resume.',
    whatYouCanDo: [
      'Add, edit, or reorder sections',
      'Change content and formatting',
      'Preview and download the resume',
    ],
    whatYouShouldDo: [
      'Save changes before leaving',
      'Use Polish for wording improvements after editing',
    ],
  },
  resumePolish: {
    title: 'Polish Resume',
    description: 'Improve wording and style of your resume with AI-assisted polish.',
    whatYouCanDo: [
      'Tweak wording and tone for clarity and impact',
      'Adjust style and formatting',
      'Preview and accept or reject changes',
    ],
    whatYouShouldDo: [
      'Run Polish before applying to new jobs',
      'Review each suggestion before applying',
    ],
  },
  resumeMassage: {
    title: 'Massage Resume',
    description: 'Tailor this resume to a specific job for a better fit.',
    whatYouCanDo: [
      'Paste a job description or use a saved job',
      'Generate a tailored version of your resume',
      'Edit and then save or go to Job Board to apply',
    ],
    whatYouShouldDo: [
      'Use for roles you are serious about applying to',
      'Review the tailored version before saving',
    ],
  },
  '/jobs': {
    title: 'Job Board',
    description: 'Find your next career opportunity. Search, save, and get personalized recommendations.',
    whatYouCanDo: [
      'Search jobs by keyword, location, and filters',
      'Save jobs to apply later from Saved Jobs',
      'View recommended jobs based on your profile',
      'Open job details and apply with a resume',
    ],
    whatYouShouldDo: [
      'Save interesting jobs so you can apply when ready',
      'Use recommendations to discover relevant roles',
      'Apply from here or from Saved Jobs with the right resume',
    ],
  },
  '/jobs/saved': {
    title: 'Saved Jobs',
    description: 'Jobs you saved for later. Apply or remove to keep your list actionable.',
    whatYouCanDo: [
      'View all saved jobs in one place',
      'Apply with a chosen resume or remove from list',
      'Open job details and filter or sort',
    ],
    whatYouShouldDo: [
      'Apply to saved jobs or remove ones you no longer want',
      'Keep the list short so you focus on real opportunities',
    ],
  },
  '/applications': {
    title: 'Application Tracking',
    description: 'Track all your applications and update status from applied to offered.',
    whatYouCanDo: [
      'View applications in list or calendar view',
      'Filter by status and update status as you progress',
      'Start a mock interview for an application',
    ],
    whatYouShouldDo: [
      'Update status when you hear back (interviewing, offered, etc.)',
      'Use Mock Interview to prepare for upcoming interviews',
    ],
  },
  '/jobs/mock-interview': {
    title: 'Mock Interview',
    description: 'Practice for interviews with AI-powered mock interviews based on your applications.',
    whatYouCanDo: [
      'Start a mock interview for a specific application',
      'Practice common and role-specific questions',
      'Review feedback and improve before the real interview',
    ],
    whatYouShouldDo: [
      'Do a mock interview before important real interviews',
      'Use applications you are actively pursuing',
    ],
  },
  mockInterviewSession: {
    title: 'Mock Interview Session',
    description: 'Your mock interview is in progress. Answer questions and get feedback.',
    whatYouCanDo: [
      'Answer questions as in a real interview',
      'Get feedback and tips during or after the session',
    ],
    whatYouShouldDo: [
      'Speak clearly and take your time',
      'Review feedback after the session',
    ],
  },
  '/jobs/:jobId/match': {
    title: 'Job Match Analysis',
    description: 'See how well your resume matches this job and get improvement suggestions.',
    whatYouCanDo: [
      'View match score and gap analysis',
      'See which skills or experience to highlight',
      'Use suggestions to polish your resume or application',
    ],
    whatYouShouldDo: [
      'Address key gaps before applying if possible',
      'Use Massage Resume to tailor for this job',
    ],
  },
  jobCreation: {
    title: 'Add Job',
    description: 'Add a job manually (e.g. from a posting) to track and apply later.',
    whatYouCanDo: [
      'Enter job title, company, and details',
      'Save and then apply with a resume from Job Board or Saved Jobs',
    ],
    whatYouShouldDo: [
      'Fill in as much detail as you have for better tracking',
    ],
  },
  jobEdit: {
    title: 'Edit Job',
    description: 'Update the job details you saved.',
    whatYouCanDo: [
      'Edit title, company, description, and other fields',
      'Save changes and apply or remove from saved',
    ],
    whatYouShouldDo: [
      'Keep details accurate for match and applications',
    ],
  },
  jobApplication: {
    title: 'Apply to Job',
    description: 'Submit your application with a resume and optional note.',
    whatYouCanDo: [
      'Choose which resume to attach',
      'Add a cover note if needed',
      'Submit the application',
    ],
    whatYouShouldDo: [
      'Pick the resume that best fits this job',
      'Double-check resume and note before submitting',
    ],
  },
  '/membership': {
    title: 'Membership',
    description: 'Manage your plan and credits for AI features like Polish, Massage, and Mock Interview.',
    whatYouCanDo: [
      'View your current plan and credit balance',
      'Upgrade or change plan',
      'Purchase or use credits for premium features',
    ],
    whatYouShouldDo: [
      'Use credits on high-value actions (e.g. polish before applying)',
      'Upgrade if you need more AI features regularly',
    ],
  },
  '/contact': {
    title: 'Contact Support',
    description: 'Get help from our team. Submit a ticket or view your support history.',
    whatYouCanDo: [
      'Submit a support ticket with your question or issue',
      'View past tickets and responses',
    ],
    whatYouShouldDo: [
      'Include enough detail so we can help quickly',
    ],
  },
  '/settings': {
    title: 'Account Settings',
    description: 'Manage your account, password, and preferences.',
    whatYouCanDo: [
      'Update profile info and password',
      'Manage notification and privacy preferences',
    ],
    whatYouShouldDo: [
      'Keep your email and password secure and up to date',
    ],
  },
  '/feedback': {
    title: 'Feedback',
    description: 'Help us improve Resumemo by sharing your experience.',
    whatYouCanDo: [
      'Submit feedback on specific features or overall experience',
      'Rate and comment on what works and what does not',
    ],
    whatYouShouldDo: [
      'Be specific so we can act on your feedback',
    ],
  },
  // Mentor pages
  '/mentor/dashboard': {
    title: 'Mentor Dashboard',
    description: 'Cohort-wide analytics for your mentees: weekly digest, KPIs, pipeline funnel, leaderboard, and a needs-attention queue.',
    whatYouCanDo: [
      'See cohort velocity, pipeline, resume health, and activity at a glance',
      'Sort the mentee leaderboard by apps, interview rate, or resume score',
      'Triage mentees from the needs-attention queue and jump into their profile',
    ],
    whatYouShouldDo: [
      'Clear the needs-attention queue weekly (open tickets, inactive mentees)',
      'Use the weekly coaching digest to plan check-ins and job assignments',
    ],
  },
  '/mentor/mentees': {
    title: 'My Mentees',
    description: 'Manage and view your mentees.',
    whatYouCanDo: [
      'View all assigned mentees',
      'Open mentee details and their applications',
    ],
    whatYouShouldDo: [
      'Review mentee progress and tickets',
    ],
  },
  menteeDetails: {
    title: 'Mentee Details',
    description: 'View and support this mentee.',
    whatYouCanDo: [
      'View mentee profile and applications',
      'Assign jobs and respond to tickets',
    ],
    whatYouShouldDo: [
      'Assign relevant jobs and provide guidance',
    ],
  },
  '/mentor/tickets': {
    title: 'Mentee Tickets',
    description: 'Support requests from your mentees.',
    whatYouCanDo: [
      'View and filter tickets',
      'Respond and resolve tickets',
    ],
    whatYouShouldDo: [
      'Respond in a timely way to help mentees',
    ],
  },
  '/mentor/job-assignments': {
    title: 'Job Assignments',
    description: 'Assign jobs to mentees to help them in their search.',
    whatYouCanDo: [
      'View assignment history',
      'Assign new jobs to mentees',
    ],
    whatYouShouldDo: [
      'Assign jobs that match mentee goals and level',
    ],
  },
  // Legal/marketing - minimal
  '/privacy-policy': {
    title: 'Privacy Policy',
    description: 'How we collect, use, and protect your data.',
  },
  '/team': {
    title: 'Our Team',
    description: 'Meet the people behind Resumemo.',
  },
  '/terms': {
    title: 'Terms of Service',
    description: 'Terms and conditions for using Resumemo.',
  },
};

/**
 * Get page description for a path. Handles dynamic routes by pattern.
 */
export function getPageDescription(pathname) {
  if (pageDescriptions[pathname]) return pageDescriptions[pathname];
  if (pathname === '/resumes/upload') return pageDescriptions['/resumes/upload'];
  if (pathname.startsWith('/resumes/') && pathname !== '/resumes' && !pathname.includes('/upload') && !pathname.includes('/polish') && !pathname.includes('/massage')) return pageDescriptions.resumeEditor;
  if (pathname.includes('/polish')) return pageDescriptions.resumePolish;
  if (pathname.includes('/massage')) return pageDescriptions.resumeMassage;
  if (pathname.startsWith('/jobs/mock-interview/session')) return pageDescriptions.mockInterviewSession;
  if (pathname.match(/^\/jobs\/[^/]+\/match$/)) return pageDescriptions['/jobs/:jobId/match'];
  if (pathname === '/jobs/new') return pageDescriptions.jobCreation;
  if (pathname.match(/^\/jobs\/[^/]+\/edit$/)) return pageDescriptions.jobEdit;
  if (pathname.match(/^\/jobs\/[^/]+\/apply$/)) return pageDescriptions.jobApplication;
  if (pathname.startsWith('/mentor/mentees/')) return pageDescriptions.menteeDetails;
  return null;
}
